import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  CreditCard,
  Receipt,
  OpenInNew,
  Cancel,
  Replay,
  Download,
} from "@mui/icons-material";
import { SPACING } from "../theme/constants";
import {
  fetchSubscription,
  fetchInvoices,
  openPortal,
  cancelBilling,
  resumeBilling,
  clearBillingError,
} from "../redux/slices/billingSlice";

const STATUS_COLORS = {
  ACTIVE: "success",
  TRIALING: "info",
  PAST_DUE: "error",
  CANCELED: "default",
  UNPAID: "error",
  INCOMPLETE: "warning",
};

const STATUS_LABELS = {
  ACTIVE: "Active",
  TRIALING: "Trial",
  PAST_DUE: "Past Due",
  CANCELED: "Canceled",
  UNPAID: "Unpaid",
  INCOMPLETE: "Incomplete",
};

export default function Billing() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscription, plan, invoices, loading, actionLoading, error } =
    useSelector((state) => state.billing);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [cancelDialog, setCancelDialog] = useState(false);

  useEffect(() => {
    dispatch(fetchSubscription());
    dispatch(fetchInvoices());
  }, [dispatch]);

  // Show success message if redirected from Stripe checkout
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      setSnackbar({
        open: true,
        message: "Payment successful! Your subscription is now active.",
        severity: "success",
      });
      // Refresh subscription data
      dispatch(fetchSubscription());
    }
  }, [searchParams, dispatch]);

  const handlePortal = async () => {
    try {
      const result = await dispatch(openPortal()).unwrap();
      if (result.data?.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.error || "Failed to open customer portal",
        severity: "error",
      });
    }
  };

  const handleCancel = async () => {
    setCancelDialog(false);
    try {
      await dispatch(cancelBilling()).unwrap();
      setSnackbar({
        open: true,
        message: "Subscription will cancel at the end of the billing period",
        severity: "info",
      });
      dispatch(fetchSubscription());
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.error || "Failed to cancel subscription",
        severity: "error",
      });
    }
  };

  const handleResume = async () => {
    try {
      await dispatch(resumeBilling()).unwrap();
      setSnackbar({
        open: true,
        message: "Subscription resumed successfully",
        severity: "success",
      });
      dispatch(fetchSubscription());
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.error || "Failed to resume subscription",
        severity: "error",
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (cents, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  if (loading && !subscription) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Billing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your subscription, payment method, and invoices
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearBillingError())}
        >
          {error}
        </Alert>
      )}

      {/* Current Plan & Subscription */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: SPACING.md,
            }}
          >
            <CreditCard sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Subscription
            </Typography>
          </Box>
          <Divider sx={{ mb: SPACING.md }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Current Plan
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {plan?.name || "Free"}
              </Typography>
              {plan && Number(plan.priceMonthly) > 0 && (
                <Typography variant="body2" color="text.secondary">
                  ${Number(plan.priceMonthly)}/month
                  {plan.priceYearly && ` · $${Number(plan.priceYearly)}/year`}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              {subscription ? (
                <>
                  <Chip
                    label={
                      STATUS_LABELS[subscription.status] || subscription.status
                    }
                    color={STATUS_COLORS[subscription.status] || "default"}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                  {subscription.cancelAtPeriodEnd && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      Cancels on {formatDate(subscription.currentPeriodEnd)}
                    </Typography>
                  )}
                </>
              ) : (
                <Chip label="No Subscription" size="small" />
              )}
            </Box>

            {subscription?.currentPeriodEnd && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Current Period
                </Typography>
                <Typography variant="body1">
                  {formatDate(subscription.currentPeriodStart)} —{" "}
                  {formatDate(subscription.currentPeriodEnd)}
                </Typography>
              </Box>
            )}

            {subscription?.trialEnd && subscription.status === "TRIALING" && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Trial Ends
                </Typography>
                <Typography variant="body1">
                  {formatDate(subscription.trialEnd)}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: SPACING.md }} />

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate("/dashboard/plans")}
            >
              {subscription?.stripeSubscriptionId
                ? "Change Plan"
                : "Upgrade Plan"}
            </Button>

            {subscription?.stripeCustomerId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNew />}
                onClick={handlePortal}
                disabled={actionLoading}
              >
                Manage Payment Method
              </Button>
            )}

            {subscription?.stripeSubscriptionId &&
              !subscription.cancelAtPeriodEnd && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<Cancel />}
                  onClick={() => setCancelDialog(true)}
                  disabled={actionLoading}
                >
                  Cancel Subscription
                </Button>
              )}

            {subscription?.cancelAtPeriodEnd && (
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<Replay />}
                onClick={handleResume}
                disabled={actionLoading}
              >
                Resume Subscription
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: SPACING.md,
            }}
          >
            <Receipt sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Invoices
            </Typography>
          </Box>
          <Divider sx={{ mb: SPACING.md }} />

          {invoices.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 4, textAlign: "center" }}
            >
              No invoices yet
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>{formatDate(invoice.createdAt)}</TableCell>
                      <TableCell>
                        {formatCurrency(
                          invoice.amountPaid || invoice.amountDue,
                          invoice.currency,
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          size="small"
                          color={
                            invoice.status === "paid"
                              ? "success"
                              : invoice.status === "open"
                                ? "warning"
                                : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(invoice.periodStart)} —{" "}
                        {formatDate(invoice.periodEnd)}
                      </TableCell>
                      <TableCell align="right">
                        {invoice.invoicePdfUrl && (
                          <Button
                            size="small"
                            startIcon={<Download />}
                            href={invoice.invoicePdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            PDF
                          </Button>
                        )}
                        {invoice.hostedInvoiceUrl && (
                          <Button
                            size="small"
                            startIcon={<OpenInNew />}
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)}>
        <DialogTitle>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your subscription will remain active until the end of the current
            billing period. After that, your organization will be downgraded to
            the Free plan. You can resume at any time before the period ends.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(false)}>
            Keep Subscription
          </Button>
          <Button onClick={handleCancel} color="error" variant="contained">
            Cancel Subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
